import {
  Box,
  Chip,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Grid,
  Tooltip,
  Theme,
  createStyles,
  withStyles,
} from '@material-ui/core';
import React, { useState } from 'react';
import axios from 'axios';
import { withRouter } from 'react-router-dom';
import { URL } from '../../config/config';

// Define TypeScript interfaces for the component props
interface AICOmponentProps {
  selectedTemplate: any | null;
  classes: any;
  onSubmit: (state: any) => void;
  currentState: any;
  closeModal: () => void;
  assistantDetails: any;
  onImageGeneration: (url: any) => void;
}

// Define interface for component state if needed
interface AICOmponentState {
  loading: boolean;
  prompt: string;
  generatedContent: string | null;
  error: string | null;
  phoneNumber: string;
  brandName: string;
}

const resolutionOptions = [
  { label: '10:16', value: 'ASPECT_10_16' },
  { label: '16:10', value: 'ASPECT_16_10' },
  { label: '9:16', value: 'ASPECT_9_16' },
  { label: '16:9', value: 'ASPECT_16_9' },
  { label: '3:2', value: 'ASPECT_3_2' },
  { label: '2:3', value: 'ASPECT_2_3' },
  { label: '4:3', value: 'ASPECT_4_3' },
  { label: '3:4', value: 'ASPECT_3_4' },
  { label: '1:1', value: 'ASPECT_1_1' },
  { label: '1:3', value: 'ASPECT_1_3' },
  { label: '3:1', value: 'ASPECT_3_1' },
];

const magicPromptTypes = [
  { label: 'Auto', value: 'AUTO' },
  { label: 'On', value: 'ON' },
  { label: 'Off', value: 'OFF' },
];

const AICOmponent: React.FC<AICOmponentProps> = ({
  classes,
  onSubmit,
  currentState,
  closeModal,
  onImageGeneration,
}) => {
  // Initialize state
  const [state, setState] = useState<AICOmponentState>({
    loading: false,
    prompt: '',
    generatedContent: null,
    error: null,
    phoneNumber: '',
    brandName: '',
  });

  // Fix: Renamed resolutionType state to avoid conflict
  const [selectedResolutionType, setSelectedResolutionType] =
    useState('ASPECT_3_4');
  const [magicPromptType, setMagicPromptType] = useState('ON');

  // Handler for prompt input change
  const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, prompt: e.target.value });
  };

  // Handler for generating content with SSE streaming
  const handleGenerate = async () => {
    if (!state.brandName.trim()) {
      setState({ ...state, error: 'Please enter a brand name' });
      return;
    }

    if (!state.phoneNumber.trim()) {
      setState({ ...state, error: 'Please enter a phone number' });
      return;
    }

    if (!state.prompt.trim()) {
      setState({ ...state, error: 'Please enter the prompt' });
      return;
    }

    setState({ ...state, loading: true, error: null });

    try {
      // Use fetch for streaming SSE response
      const response = await fetch(
        `${URL}/generate-instagram-ad-image?testKey=FDanxlOvq5`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt: state.prompt,
            aspectRatio: selectedResolutionType,
            phoneNumber: state.phoneNumber,
            userName: state.brandName,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is not readable');
      }

      let buffer = '';
      const imageUrls: string[] = [];
      const concepts: any[] = [];
      let hasError = false;
      let errorMessage = '';

      // Read the stream
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('Stream complete');
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete messages in buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim() || line.trim() === 'data:') continue;

          // Remove "data: " prefix
          const cleanLine = line.replace(/^data:\s*/, '').trim();
          if (!cleanLine) continue;

          try {
            const chunkData = JSON.parse(cleanLine);
            console.log('Received chunk:', chunkData);

            // Handle different stages
            if (chunkData.stage === 'concept' && chunkData.data) {
              concepts.push(chunkData.data);
              console.log('Concept received:', chunkData.data);
            } else if (chunkData.stage === 'image_complete' && chunkData.data) {
              imageUrls.push(chunkData.data);
              console.log('Image URL received:', chunkData.data);
            } else if (chunkData.stage === 'error') {
              hasError = true;
              errorMessage = chunkData.error || 'Unknown error occurred';
              console.error('Error in stream:', errorMessage);
            } else if (chunkData.stage === 'heartbeat') {
              console.log('Heartbeat:', chunkData.message);
            }
          } catch (parseError) {
            console.error('Error parsing JSON:', parseError, 'Line:', cleanLine);
          }
        }
      }

      // Process collected data
      if (hasError) {
        throw new Error(errorMessage || 'Failed to generate image');
      }

      if (imageUrls.length === 0) {
        throw new Error('No images were generated');
      }

      // Map the streaming response to the existing preview structure
      const payload: any = {
        imageResult1: {
          created: new Date().toISOString(),
          data: [
            {
              is_image_safe: true,
              prompt: state.prompt,
              resolution: selectedResolutionType || '1:1',
              seed: 0,
              style_type: null,
              url: imageUrls[0],
            },
          ],
        },
      };

      // Add second image if available
      if (imageUrls[1]) {
        payload.imageResult2 = {
          created: new Date().toISOString(),
          data: [
            {
              is_image_safe: true,
              prompt: state.prompt,
              resolution: selectedResolutionType || '1:1',
              seed: 1,
              style_type: null,
              url: imageUrls[1],
            },
          ],
        };
      }

      onImageGeneration(payload);
      setState({ ...state, loading: false, error: null });
    } catch (error: any) {
      console.error('Generation error:', error);
      setState({
        ...state,
        loading: false,
        error: error.message || 'Failed to generate image',
      });
    }
  };

  const handleChange = (e: any) => {
    const input = e.target.value;
    const lastTypedChar =
      input.length > state.phoneNumber.length ? input.slice(-1) : '';

    if (input.length < state.phoneNumber.length) {
      setState({
        ...state,
        phoneNumber: state.phoneNumber.slice(0, input.length),
      });
    } else if (lastTypedChar) {
      setState({ ...state, phoneNumber: state.phoneNumber + lastTypedChar });
    }
  };

  const getStars = () => {
    const length = state.phoneNumber.length;

    if (length <= 6) {
      // Show actual numbers for first 6 digits
      return state.phoneNumber;
    } else {
      // For length > 6, show stars for all except last 4 digits
      const visibleDigits = 4;
      const starsCount = length - visibleDigits;
      return '*'.repeat(starsCount) + state.phoneNumber.slice(-visibleDigits);
    }
  };

  return (
    <Grid container spacing={3} style={{ fontFamily: 'Inter, sans-serif' }}>
      <Grid item xs={12}>
        <Box>
          <Typography
            variant="h4"
            style={{
              marginBottom: '12px',
              fontSize: window.innerWidth < 768 ? '16px' : '24px',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Brand Details:
          </Typography>
          <Box
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              marginBottom: '16px',
              flexDirection: window.innerWidth < 768 ? 'column' : 'row',
            }}
          >
            <TextField
              fullWidth
              value={state.brandName}
              variant="outlined"
              placeholder="Enter your brand name"
              onChange={(e) =>
                setState({ ...state, brandName: e.target.value })
              }
              InputProps={{
                style: { fontFamily: 'Inter, sans-serif' },
              }}
            />
            <TextField
              // value={state.phoneNumber}
              value={getStars()}
              fullWidth
              variant="outlined"
              placeholder="Enter your WhatsApp number"
              // onChange={(e) =>
              //   setState({ ...state, phoneNumber: e.target.value })
              // }
              onChange={handleChange}
              InputProps={{
                style: { fontFamily: 'Inter, sans-serif' },
              }}
            />
          </Box>
        </Box>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h4"
            style={{
              marginBottom: '12px',
              fontSize: window.innerWidth < 768 ? '16px' : '24px',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Write your prompt:
          </Typography>
        </Box>

        <TextField
          inputProps={{
            maxLength: 1024,
            style: { fontFamily: 'Inter, sans-serif' },
          }}
          fullWidth
          variant="outlined"
          value={state.prompt}
          onChange={handlePromptChange}
          placeholder="E.g., Create a compelling ad for a fitness app targeting young adults"
          multiline
          rows={4}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleGenerate();
            }
          }}
        />
        {state.error && (
          <Grid item xs={12} style={{ marginTop: '16px' }}>
            <Typography
              color="error"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {state.error}
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* <Grid item xs={12}>
        <Box>
          <Typography variant="h4" style={{ marginBottom: '12px' }}>
            Magic Prompt:
          </Typography>
          <Box style={{ display: 'flex', flexWrap: 'wrap', margin: '-4px' }}>
            {magicPromptTypes.map((type) => (
              <Box style={{ padding: '4px' }} key={type.value}>
                <Chip
                  label={type.label}
                  onClick={() => setMagicPromptType(type.value)}
                  variant="outlined"
                  style={{
                    borderRadius: '6px',
                    backgroundColor:
                      magicPromptType === type.value ? '#e9e1ff' : 'white',
                    color: magicPromptType === type.value ? '#772DF3' : 'black',
                    border: `1px solid ${
                      magicPromptType === type.value ? '#772DF3' : '#E8E8E8'
                    }`,
                    padding: '16px 8px',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Grid> */}

      {/* <Grid item xs={12}>
        <Box>
          <Typography
            variant="h4"
            style={{ marginBottom: '12px', fontSize: '28px' }}
          >
            Resolution:
          </Typography>
          <Box style={{ display: 'flex', flexWrap: 'wrap', margin: '-4px' }}>
            {resolutionOptions.map((type) => (
              <Box style={{ padding: '4px' }} key={type.value}>
                <Chip
                  label={type.label}
                  onClick={() => setSelectedResolutionType(type.value)}
                  variant="outlined"
                  style={{
                    borderRadius: '6px',
                    backgroundColor:
                      selectedResolutionType === type.value
                        ? '#e9e1ff'
                        : 'white',
                    color:
                      selectedResolutionType === type.value
                        ? '#772DF3'
                        : 'black',
                    border: `1px solid ${
                      selectedResolutionType === type.value
                        ? '#772DF3'
                        : '#E8E8E8'
                    }`,
                    padding: '16px 8px',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Grid> */}

      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%', // Added this line
          marginTop: '5px',
        }}
      >
        <Button
          variant="contained"
          onClick={handleGenerate}
          style={{
            width: '98%',
            marginTop: '16px',
            color: 'white',
            background:
              'linear-gradient(90deg, #41b862 0%, #3aa558 46.36%, #339249 59.52%, #2c7f3f 100%)',
            border: '1px solid #3aa558',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          <Box
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
            }}
          >
            {state.loading ? (
              <>
                <CircularProgress size={28} />
                <span
                  style={{ fontSize: '24px', fontFamily: 'Inter, sans-serif' }}
                >
                  Generating...
                </span>
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={28}
                  height={28}
                  fill="white"
                  viewBox="0 0 25 25"
                >
                  <path
                    d="M6.37954 14.0132L10.1522 12.1269C11.2843 11.5608 12.2022 10.6429 12.7682 9.51079L14.6553 5.73745C14.716 5.616 14.8094 5.51386 14.9249 5.44247C15.0404 5.37108 15.1735 5.33327 15.3093 5.33327C15.4451 5.33327 15.5782 5.37108 15.6937 5.44247C15.8092 5.51386 15.9025 5.616 15.9633 5.73745L17.8497 9.51012C18.4157 10.6422 19.3336 11.5602 20.4657 12.1263L24.2377 14.0132C24.3592 14.0739 24.4614 14.1672 24.5328 14.2827C24.6042 14.3983 24.642 14.5314 24.642 14.6672C24.642 14.803 24.6042 14.9361 24.5328 15.0516C24.4614 15.1672 24.3592 15.2605 24.2377 15.3212L20.465 17.2076C19.3329 17.7736 18.415 18.6915 17.849 19.8236L15.9626 23.5963C15.9019 23.7177 15.8085 23.8199 15.693 23.8912C15.5775 23.9626 15.4444 24.0004 15.3086 24.0004C15.1728 24.0004 15.0397 23.9626 14.9242 23.8912C14.8087 23.8199 14.7153 23.7177 14.6546 23.5963L12.7682 19.8236C12.2022 18.6915 11.2843 17.7736 10.1522 17.2076L6.37954 15.3205C6.25822 15.2598 6.15621 15.1664 6.08491 15.051C6.01362 14.9355 5.97586 14.8025 5.97586 14.6669C5.97586 14.5312 6.01362 14.3982 6.08491 14.2827C6.15621 14.1673 6.25822 14.074 6.37954 14.0132ZM6.85181 5.08932L8.46861 4.27985C8.52052 4.25378 8.56417 4.21379 8.59466 4.16434C8.62516 4.1149 8.64131 4.05795 8.64131 3.99985C8.64131 3.94176 8.62516 3.88481 8.59466 3.83537C8.56417 3.78592 8.52052 3.74593 8.46861 3.71985L6.85181 2.91119C6.36661 2.66862 5.97318 2.27519 5.73061 1.78999L4.92194 0.173188C4.89587 0.121275 4.85588 0.0776322 4.80643 0.0471354C4.75699 0.0166385 4.70004 0.000488281 4.64194 0.000488281C4.58385 0.000488281 4.5269 0.0166385 4.47746 0.0471354C4.42801 0.0776322 4.38802 0.121275 4.36194 0.173188L3.55328 1.78999C3.31071 2.27519 2.91728 2.66862 2.43208 2.91119L0.815278 3.71985C0.763365 3.74593 0.719722 3.78592 0.689225 3.83537C0.658728 3.88481 0.642578 3.94176 0.642578 3.99985C0.642578 4.05795 0.658728 4.1149 0.689225 4.16434C0.719722 4.21379 0.763365 4.25378 0.815278 4.27985L2.43221 5.08839C2.9174 5.33092 3.31082 5.7243 3.55341 6.20946L4.36194 7.82652C4.38802 7.87844 4.42801 7.92208 4.47746 7.95257C4.5269 7.98307 4.58385 7.99922 4.64194 7.99922C4.70004 7.99922 4.75699 7.98307 4.80643 7.95257C4.85588 7.92208 4.89587 7.87844 4.92194 7.82652L5.73034 6.20959C5.97303 5.72444 6.36655 5.33178 6.85181 5.08932Z"
                    fill={'white'}
                  />
                </svg>
                <span
                  className={classes.text}
                  style={{
                    marginLeft: '6px',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Generate your AI creative
                </span>
              </>
            )}
          </Box>
        </Button>
      </Box>
    </Grid>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    text: {
      fontSize: '24px',
      [theme.breakpoints.down('sm')]: {
        fontSize: '17px',
      },
    },
  });

export default withStyles(styles)(AICOmponent);
